import type { Editor } from '@tiptap/core'
import type { LucideIcon } from 'lucide-react'
import type { ShortcutKeysInput } from '@/components/util/shortcuts'

import { CELL_HIGHLIGHT_COLORS, CELL_TEXT_COLORS } from '@rundown-studio/utils'
import { useEditorState } from '@tiptap/react'

import { MenuRadioItemGroup } from '@ark-ui/react'

import {
  BaselineIcon,
  BoldIcon,
  CaseSensitiveIcon,
  CheckIcon,
  ChevronDownIcon,
  Heading1Icon,
  Heading2Icon,
  Heading3Icon,
  ItalicIcon,
  ListChecksIcon,
  ListIcon,
  ListOrderedIcon,
  MinusIcon,
  RemoveFormattingIcon,
  StrikethroughIcon,
  TypeIcon,
} from 'lucide-react'

import { ToolbarSeparator } from '@/components/ui/Toolbar'
import {
  CellToolbarItem,
  CellToolbarMenu,
  CellToolbarMenuItem,
} from './CellToolbar'
import { KbdGrouper } from '@/components/ui/kbd'

type StyleBlock = {
  id: string
  label: string
  icon: LucideIcon
  isActive: (editor: Editor) => boolean
  command: (editor: Editor) => void
  shortcut: ShortcutKeysInput
}

export const STYLE_BLOCKS = [
  {
    id: 'paragraph',
    label: 'Normal Text',
    icon: TypeIcon,
    isActive: (editor) =>
      !editor.isActive('heading') &&
      !editor.isActive('bulletList') &&
      !editor.isActive('orderedList') &&
      !editor.isActive('taskList'),
    command: (editor) => editor.chain().focus().setParagraph().run(),
    shortcut: [['mod', 'option', '0']],
  },
  {
    id: 'h1',
    label: 'Heading 1',
    icon: Heading1Icon,
    isActive: (editor) => editor.isActive('heading', { level: 1 }),
    command: (editor) =>
      editor.chain().focus().toggleHeading({ level: 1 }).run(),
    shortcut: [['mod', 'option', '1']],
  },
  {
    id: 'h2',
    label: 'Heading 2',
    icon: Heading2Icon,
    isActive: (editor) => editor.isActive('heading', { level: 2 }),
    command: (editor) =>
      editor.chain().focus().toggleHeading({ level: 2 }).run(),
    shortcut: [['mod', 'option', '2']],
  },
  {
    id: 'h3',
    label: 'Heading 3',
    icon: Heading3Icon,
    isActive: (editor) => editor.isActive('heading', { level: 3 }),
    command: (editor) =>
      editor.chain().focus().toggleHeading({ level: 3 }).run(),
    shortcut: [['mod', 'option', '3']],
  },
  {
    id: 'bulletList',
    label: 'Bullet List',
    icon: ListIcon,
    isActive: (editor) => editor.isActive('bulletList'),
    command: (editor) => editor.chain().focus().toggleBulletList().run(),
    shortcut: [['mod', 'shift', '8']],
  },
  {
    id: 'orderedList',
    label: 'Number List',
    icon: ListOrderedIcon,
    isActive: (editor) => editor.isActive('orderedList'),
    command: (editor) => editor.chain().focus().toggleOrderedList().run(),
    shortcut: [['mod', 'shift', '7']],
  },
  {
    id: 'taskList',
    label: 'Task List',
    icon: ListChecksIcon,
    isActive: (editor) => editor.isActive('taskList'),
    command: (editor) => editor.chain().focus().toggleTaskList?.().run(),
    shortcut: [['mod', 'shift', '9']],
  },
] as const satisfies StyleBlock[]

export function CellToolbarEditorItems({ editor }: { editor: Editor }) {
  const { activeItem, hasActiveBlock } = useEditorState({
    editor: editor!,
    selector: (ctx) => {
      const activeEditor = ctx.editor

      const activeMatch = STYLE_BLOCKS.find((item) =>
        item.isActive(activeEditor),
      )

      const isModifiedStyle =
        activeEditor.isActive('heading') ||
        activeEditor.isActive('bulletList') ||
        activeEditor.isActive('orderedList') ||
        activeEditor.isActive('taskList')

      return {
        hasActiveBlock: isModifiedStyle,
        activeItem: activeMatch || STYLE_BLOCKS[0], // Fallback to paragraph
      }
    },
  })

  const { isBold, isItalic, isStrikethrough } = useEditorState({
    editor: editor!,
    selector: (ctx) => ({
      isBold: ctx.editor.isActive('bold'),
      isItalic: ctx.editor.isActive('italic'),
      isStrikethrough: ctx.editor.isActive('strike'),
    }),
  })

  const { activeTextColor, activeHighlightColor } = useEditorState({
    editor: editor!,
    selector: (ctx) => {
      const activeColor =
        ctx.editor.getAttributes('textStyle').color || '#ffffff'
      const activeHigh =
        ctx.editor.getAttributes('highlight').color || 'transparent'

      return {
        activeTextColor: activeColor,
        activeHighlightColor: activeHigh,
      }
    },
  })

  const BlockTriggerIcon = activeItem.icon

  return (
    <>
      {/* BLOCK STYLE */}
      <CellToolbarMenu
        id={'blockstyle'}
        trigger={
          <CellToolbarItem
            active={hasActiveBlock}
            aria-pressed={undefined} // a11y: menus shouldn't have `pressed` even if active
            tooltip={`Text style: ${activeItem.label}`}
            aria-label="Text style" // a11y: stable name
            aria-description={`Current style: ${activeItem.label}`} // a11y: contextual value
          >
            <BlockTriggerIcon />
            <ChevronDownIcon className="text-neutral-500" />
          </CellToolbarItem>
        }
      >
        <MenuRadioItemGroup
          value={activeItem.id}
          onValueChange={({ value }) => {
            const selected = STYLE_BLOCKS.find((i) => i.id === value)
            if (!selected) return
            selected.command(editor) // Execute Tiptap command
          }}
        >
          {STYLE_BLOCKS.map((item) => {
            const ItemIcon = item.icon
            return (
              <CellToolbarMenuItem type="radio" key={item.id} value={item.id}>
                <span className="flex items-center gap-2 mr-1">
                  <ItemIcon className="size-4 opacity-40 group-data-[state=checked]:opacity-100" />
                  <span>{item.label}</span>
                  <CheckIcon className="size-3 ml-auto hidden group-data-[state=checked]:block text-neutral-200" />
                </span>

                <KbdGrouper keys={item.shortcut} />
              </CellToolbarMenuItem>
            )
          })}
        </MenuRadioItemGroup>
      </CellToolbarMenu>

      {/* COLORS */}
      <CellToolbarMenu
        id={'colors'}
        trigger={
          <CellToolbarItem tooltip="Text colors" aria-label="Text colors">
            <div
              className="w-5 h-5 -m-1 mr-0 rounded-sm border border-black/15 flex items-center justify-center"
              style={{
                backgroundColor:
                  activeHighlightColor !== 'transparent'
                    ? activeHighlightColor
                    : undefined,
              }}
            >
              <BaselineIcon
                style={{
                  color: activeTextColor,
                }}
              />
            </div>
            <ChevronDownIcon className="text-neutral-500" />
          </CellToolbarItem>
        }
      >
        <DropdownColorGrid
          id="text-color"
          label="Text color"
          colors={CELL_TEXT_COLORS}
          value={activeTextColor}
          onValueChange={({ value }) => {
            if (value === 'text-color-default') {
              editor.chain().focus().unsetColor().run()
            } else {
              const color = CELL_TEXT_COLORS.find((c) => c === value)
              if (!color) return
              // Execute Tiptap command
              editor.chain().focus().setColor(color).run()
            }
          }}
        />

        <ToolbarSeparator orientation="horizontal" />

        <DropdownColorGrid
          id="highlight-color"
          label="Highlight color"
          colors={CELL_HIGHLIGHT_COLORS}
          isHighlights={true}
          value={activeHighlightColor}
          onValueChange={({ value }) => {
            if (value === 'highlight-color-default') {
              editor.chain().focus().unsetHighlight().run()
            } else {
              const color = CELL_HIGHLIGHT_COLORS.find((c) => c === value)
              if (!color) return
              // Execute Tiptap command
              editor.chain().focus().setHighlight({ color: color }).run()
            }
          }}
        />
      </CellToolbarMenu>

      <ToolbarSeparator />

      <CellToolbarItem
        tooltip="Bold"
        tooltipShortcut={[['mod', 'b']]}
        active={isBold}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <BoldIcon />
      </CellToolbarItem>

      <CellToolbarItem
        tooltip="Italic"
        tooltipShortcut={[['mod', 'i']]}
        active={isItalic}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <ItalicIcon />
      </CellToolbarItem>

      <CellToolbarItem
        tooltip="Strikethrough"
        tooltipShortcut={[['mod', 'shift', 's']]}
        active={isStrikethrough}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <StrikethroughIcon />
      </CellToolbarItem>

      <ToolbarSeparator />

      <CellToolbarItem
        tooltip="Clear formatting"
        tooltipShortcut={[['mod', '\\']]}
        onClick={() =>
          editor.chain().focus().unsetAllMarks().clearNodes().run()
        }
      >
        <RemoveFormattingIcon />
      </CellToolbarItem>

      <CellToolbarItem
        tooltip="Insert separator"
        tooltipShortcut={[['mod', 'shift', '-'], '---']}
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        <MinusIcon />
      </CellToolbarItem>

      <CellToolbarItem
        tooltip="Toggle text case"
        tooltipShortcut={[['mod', 'shift', 'u']]}
        onClick={() => editor.chain().focus().toggleCase().run()}
      >
        <CaseSensitiveIcon />
      </CellToolbarItem>

      <ToolbarSeparator />
    </>
  )
}

function DropdownColorGrid({
  id,
  label,
  value,
  onValueChange,
  colors,
  isHighlights = false,
}: {
  id: string
  label: string
  colors: string[]
  value: string
  onValueChange: React.ComponentProps<
    typeof MenuRadioItemGroup
  >['onValueChange']
  isHighlights?: boolean
}) {
  return (
    <>
      <div
        id={id}
        className="px-1 mb-1.5 text-xs font-semibold text-neutral-600 tracking-wider uppercase select-none"
      >
        {label}
      </div>

      <MenuRadioItemGroup
        value={value}
        onValueChange={onValueChange}
        aria-labelledby={id}
        className="grid grid-cols-5 gap-1 justify-items-center"
      >
        {/* Reset item */}
        <CellToolbarMenuItem
          type="radio"
          value={`${id}-default`}
          className="p-0 w-8 h-8 rounded-md flex items-center justify-center border border-transparent relative overflow-hidden data-highlighted:border-neutral-700 group"
          aria-label={
            isHighlights ? 'Remove highlight' : 'Reset text to default color'
          }
        >
          <div className="absolute inset-0 p-1 flex items-center justify-center">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              className="w-full h-full text-red-500/80 stroke-3"
              aria-hidden="true"
            >
              <line x1="22" y1="2" x2="2" y2="22" />
            </svg>
          </div>
          {!isHighlights && (
            <span
              className="text-base font-bold text-neutral-400 mix-blend-screen z-10"
              aria-hidden="true"
            >
              A
            </span>
          )}
        </CellToolbarMenuItem>

        {/* Colors item loop */}
        {colors.map((color) => (
          <CellToolbarMenuItem
            type="radio"
            key={`${id}-${color}`}
            value={color}
            className="p-0 w-8 h-8 rounded-md flex items-center justify-center border border-transparent data-highlighted:border-neutral-700 data-[state=checked]:border-blue-500/80 data-[state=checked]:bg-blue-500/10 group relative"
            aria-label={
              isHighlights
                ? `Set highlight color to ${color}`
                : `Set text color to ${color}`
            }
          >
            {isHighlights ? (
              <span
                className="w-5 h-5 rounded border border-white/10"
                style={{ backgroundColor: color }}
                title={color}
                aria-hidden="true"
              />
            ) : (
              <span
                className="text-base font-semibold"
                style={{ color: color }}
                title={color}
                aria-hidden="true"
              >
                A
              </span>
            )}
          </CellToolbarMenuItem>
        ))}
      </MenuRadioItemGroup>
    </>
  )
}
